import { Proposal, ProposalStatus } from "./types";

type WithStatus = Pick<Proposal, "status">;
type WithActions = Pick<Proposal, "status" | "actions">;

// A draft: the rule for Delete, Save and Regenerate on a proposal not stored yet.
export const isDraftOnlyActionAllowed = (proposal: WithStatus) =>
  proposal.status === ProposalStatus.DRAFT;

// contractsapi says what a stored proposal allows; one not stored yet is a
// draft only this editor holds.
const allows = (proposal: WithActions, action: string) =>
  proposal.actions
    ? proposal.actions.includes(action)
    : isDraftOnlyActionAllowed(proposal);

export const isDeletable = (proposal: WithActions) =>
  allows(proposal, "delete");

// A stored draft can't be saved once its deal is closed.
export const isSavable = (proposal: WithActions) => allows(proposal, "save");

// contractsapi offers Send on an open draft, and again while its Send runs.
export const isSendable = (proposal: WithActions) => allows(proposal, "send");

// A Send has started: contractsapi offers Send again but no longer Save, so
// the draft stays as it went out until the Send finishes or it is revised.
export const isSendInProgress = (proposal: WithActions) =>
  !!proposal.actions &&
  isDraftOnlyActionAllowed(proposal) &&
  proposal.actions.includes("send") &&
  !proposal.actions.includes("save");

// Out for signature, part-signed included: there is a request to withdraw.
export const isAwaitingSignature = (proposal: WithStatus) =>
  proposal.status === ProposalStatus.SENT ||
  proposal.status === ProposalStatus.DELIVERED;

// contractsapi offers Revise on anything that went out but wasn't signed, and on
// a draft whose Send started. A proposal not stored yet has nothing to revise.
export const isRevisable = (proposal: Pick<Proposal, "actions">) =>
  !!proposal.actions?.includes("revise");

// contractsapi offers Withdraw while a request is out for signature: sent,
// part-signed, or a draft whose Send started, on an open or a closed deal.
export const isWithdrawable = (proposal: Pick<Proposal, "actions">) =>
  !!proposal.actions?.includes("withdraw");

// Revise withdraws the request first while one is out: sent, part-signed, or
// a Send that started. One that already ended returns to Draft as it is.
// The same rule contractsapi lists `withdraw` by, read here from the status.
export const withdrawsOnRevise = (proposal: WithActions) =>
  isAwaitingSignature(proposal) || isSendInProgress(proposal);

// Still in play: a draft, or out for signature.
export const isActive = (proposal: WithStatus) =>
  isDraftOnlyActionAllowed(proposal) || isAwaitingSignature(proposal);

// Regenerate rebuilds a draft the rep then saves, so it is offered only where
// one can be saved: a draft contractsapi still lets be saved (not once its Send
// started or its deal closed), or one out for signature that Revise returns to
// Draft first. One that ended stays as it ended.
export const isRegenerable = (proposal: WithActions) =>
  isDraftOnlyActionAllowed(proposal)
    ? isSavable(proposal)
    : isAwaitingSignature(proposal) && isRevisable(proposal);
